// The parts the bell's two views are built from: the header, with its two
// links, and a notification's row.
import type { ReactNode } from "react";
import type { useSwipe } from "@/platform/client";
import { cx } from "@/platform/ui";
import { notificationTones } from "@/notifications/ui/presenters.js";

type GrabHandlers = ReturnType<typeof useSwipe>["handlers"];

/** With `grab`, the header is where the window is taken, and shows its grab
 *  line; without, nothing moves it. */
export function PanelHeader({
  label,
  onAction,
  actionDisabled,
  onSeeAll,
  grab,
}: {
  label: string;
  onAction(): void;
  actionDisabled?: boolean;
  onSeeAll?(): void;
  grab?: GrabHandlers;
}) {
  return (
    <div
      className={cx(
        "flex flex-col border-b border-line bg-surface-2 px-3",
        grab ? "py-1.5" : "py-2",
      )}
      style={grab ? { touchAction: "pan-y" } : undefined}
      {...grab}
    >
      {grab && (
        <div className="flex justify-center pb-1" aria-hidden="true">
          <span className="h-[3px] w-8 rounded-full bg-line-strong" />
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] font-bold uppercase tracking-wider text-muted">
          Notifications
        </span>
        <div className="flex items-center gap-3">
          {onSeeAll && (
            <button onClick={onSeeAll} className="text-[10px] font-bold text-faint hover:text-ink">
              See all
            </button>
          )}
          <button
            onClick={onAction}
            disabled={actionDisabled}
            className="text-[10px] font-bold text-faint hover:enabled:text-ink disabled:cursor-not-allowed disabled:opacity-40"
          >
            {label}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface PanelItem {
  id: string | number;
  icon?: ReactNode;
  title: string;
  body?: string | null;
  hasHref: boolean;
  timeLabel: string;
  onOpen(): void;
}

/** One notification. `unread` false shows it read; `aside` sits under its
 *  time. */
export function PanelRow({
  item,
  unread = true,
  aside,
}: {
  item: PanelItem;
  unread?: boolean;
  aside?: ReactNode;
}) {
  const tone = notificationTones(unread);
  return (
    <div className="group flex items-start gap-2.5 px-3 py-2.5 hover:bg-surface-2">
      <button
        onClick={item.onOpen}
        className={cx(
          "flex min-w-0 flex-1 items-start gap-2.5 text-left",
          item.hasHref && "cursor-pointer",
        )}
      >
        {item.icon && <span className={cx("flex-none self-center", tone.icon)}>{item.icon}</span>}
        <div className="min-w-0 flex-1">
          <div className={cx("truncate text-xs font-bold", tone.title)}>{item.title}</div>
          {item.body && (
            <div className={cx("mt-0.5 text-[11px] leading-snug", tone.body)}>{item.body}</div>
          )}
          {item.hasHref && <div className="mt-1 text-[10px] font-bold text-accent">View</div>}
        </div>
      </button>
      <div className="flex flex-none flex-col items-end gap-1">
        <div className="whitespace-nowrap text-[9px] font-semibold text-faint">
          {item.timeLabel}
        </div>
        {aside}
      </div>
    </div>
  );
}
